<template>
  <div class="details-component">
    <div class="details-component__header">

      <h1>{{ $t('common.products_delay') }}</h1>

        <div class="details-component__header_right">

          <a v-if="!isProductFormOpened" class="link" v-on:click="toggleProductForm">
            {{ $t('products.add_product') }}
            <i class="icon icon--plus"></i>
          </a>

          <a v-else class="disabled link" v-on:click="toggleProductForm" v-bind:class="{ active: isActive }">
            {{ $t('common.close') }}
            <i class="icon icon--cross"></i>
          </a>

        </div>

    </div>

    <autocomplete class="details-component__lookupform inline-search"
      lookup="products"
      :filter="autocompleteProductFilter"
      v-on:query="selectProduct"
      v-bind:class="{ active: isActive }" v-if="isProductFormOpened">
      <template slot="inner-item" slot-scope="props">
        <span>{{props.item.name}}</span>
        <span>
          <span class="disabled">{{props.item.total}}/</span>
          <span>{{props.item.issued}}</span>
        </span>
      </template>
    </autocomplete>

    <div class="details-component__lookupform details-component__lookupform-confirm"
       v-if="isProductFormOpened && product !== null"
       v-bind:class="{ active: isActive }">
      <form class="form">

         <div class="form__field">
          <label class="form__label">{{ $t('common.product') }}</label>
          <div>{{product.name}}</div>
         </div>

         <div class="form__field" :class="{'form__field_error' : errors.has('amount')}" v-if="product.type === 'permitPack'">
          <label for="amount" class="form__label">{{ $t('common.amount') }}</label>
          <input type="text"
            id="amount"
            class="form__input"
            v-model="form.amount"
            v-validate
            data-vv-rules="required|numeric|max:4|cannotBeLower"
            name="amount"
            ref="amount"
             />
         </div>

         <div class="form__field" :class="{'form__field_error' : errors.has('date_application')}">
           <label class="form__label">{{ $t('common.date') }}</label>
           <el-date-picker
              v-model="form.date_application"
              format="dd.MM.yyyy"
              class="form__input"
              type="datetime"
              name="date_application"
              :default-time="defaultTime"
              :picker-options="timeOptions"
            >
            </el-date-picker>
         </div>

         <div class="form__buttons buttonset">
          <button
            type="button"
            class="buttonset__button btn btn--secondary"
            v-on:click.prevent.stop="toggleProductForm()">
            {{ $t('common.discard') }}
          </button>
          <button
            type="submit"
            class="buttonset__button btn btn--main"
            v-on:click.prevent.stop="addProduct()">
            {{ $t('products.submit') }}
         </button>
        </div>
      </form>
    </div>

    <div class="details-component__content content" v-if="products">
      <v-client-table class="mobile-1070" :data="products" :columns="columns" :options="options">
          <span slot="type" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('products.type') }}: </span><span class="total">{{ $t(`product.${props.row.type}`) }}</span></span>
          <span slot="name" slot-scope="props" class="mobile-padding"><span class="mobile">{{ $t('common.name') }}: </span>
              <a class="link" @click="routeToProduct(props.row)">
                <span>{{ props.row.name }}</span>
              </a>
          </span>
          <span slot="total" slot-scope="props" class="total" v-show="props.row.type === 'permitPack'">{{props.row.total}}</span>
          <span slot="zone" slot-scope="props"><span v-for="(zone, index) in props.row.zones" class="mobile-padding"><span class="mobile">{{ $t('common.zone') }}: </span>
            <span>{{ zone.code }}
              <span v-show="isLengthSmaller(index, props.row.zones.length)">,&nbsp;</span>
            </span>
          </span></span>
          <span slot="available" slot-scope="props" class="mobile-padding"><span class="mobile" v-show="props.row.type === 'permitPack'">{{ $t('products.parking_spots') }}: </span>
              <span v-show="props.row.type === 'permitPack'">
                <span class="total" v-show="props.row.available >= 0">{{ props.row.available }}</span>
                <span class="total orange" v-show="props.row.available < 0">{{ $t('products.exceeded_by', { amount: Math.abs(props.row.available) }) }}</span>
              </span>
          </span>
          <div slot="remove_product" slot-scope="props" class="delete-wrapper">
            <a @click="showConfirmationForRemovingProduct(props.row)" :id="props.row.id">
              <span class="delete-wrapper__icon icon icon--cross"></span>
            </a>
          </div>
          <div slot="count" slot-scope="props" v-show="props.row.type === 'permitPack'" class="mobile-padding">
            <span class="mobile">{{ $t('common.issued') }}: </span>
            <span class="total-permit-count" v-show="!isSelectedRow(props.row.id)" @click="setSelectedRow(props.row)">
              {{props.row.count}}
              <a class="edit-wrapper">
                {{ $t('common.change') }}
                <i class="icon icon--arrow" />
              </a>
            </span>
            <form class="form" v-show="isSelectedRow(props.row.id)">
              <input @input=" e => editTotalPermitAmount('permitInput', e.target.value)"
                name="permit_input"
                class="form__input--editing"
                :value="props.row.count"
                :ref="'changeCount' + props.row.id"
                @keydown.prevent.stop.enter="submitEdit(props.row.id, props.row.product_id)"
              />
              <div class="form__submit-edit" @click="submitEdit(props.row.id, props.row.product_id)"></div>
            </form>
          </div>
      </v-client-table>
    </div>

    <ConfirmationModal v-if="showConfirmationModal" @cancel="cancelProductRemove" @confirmed="commitRemoveProduct">
      <h1 slot="header">{{ $t('tenants.removing') }} {{ removeProduct.name }} </h1>
      <p slot="body">{{ $t('products.confirm_remove') }}</p>
    </ConfirmationModal>

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ productWarningHeader }}</h1>
      <p slot="body">{{ productWarningBody }}</p>
    </WarningModal>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment';
  import * as mTypes from 'store/mutation-types';
  import ListWidget from 'common/ListWidget';
  import Autocomplete from 'common/Autocomplete';
  import Tooltip from 'common/Tooltip';
  import ConfirmationModal from 'common/ConfirmationModal';
  import WarningModal from 'common/WarningModal';

  export default {
    props: ['products'],
    components: {
      ListWidget,
      Autocomplete,
      Tooltip,
      ConfirmationModal,
      WarningModal,
    },

    computed: {
      ...mapGetters({
        tenant: 'tenants/tenant/tenant',
        tenantName: 'tenants/tenant/name',
        productsError: 'tenants/product/error',
        user: 'user/user/user',
        tenantsLimit: 'landlord/tenants/limit',
        keywordFilter: 'landlord/tenants/keywordFilter',
      }),
      defaultTime() {
        return moment().set('second', 0).format('HH:mm:ss');
      },
    },
    created() {
      this.$validator.remove('cannotBeLower');
      this.createAmountValidator();
    },
    methods: {
      toggleProductForm() {
        this.isProductFormOpened = !this.isProductFormOpened;
        this.isActive = !this.isActive;
        if (this.isProductFormOpened) {
          this.$nextTick(() => {
            this.$el.getElementsByClassName('autocomplete__input')[0].focus();
          });
        } else {
          this.product = null;
          this.form.amount = null;
        }
      },
      autocompleteProductFilter(data) {
        if (!this.isSuperAdmin()) {
          return data;
        }
        return data.filter(value => value.partner_id === this.tenant.landlord.id);
      },
      selectProduct(q) {
        this.product = q.item;
        this.$nextTick(() => {
          if (this.product.type === 'permitPack') {
            this.$refs.amount.focus();
          }
        });
      },
      addProduct() {
        this.$validator.validateAll()
          .then((success) => {
            if (!success) {
              return;
            }
            const amount = parseInt(this.form.amount, 10);
            const tenantId = parseInt(this.$route.params.id, 10);
            this.$store.dispatch('tenants/productDelayed/add', {
              tenant_id: tenantId,
              product_id: this.product.id,
              date_application: this.form.date_application,
              amount,
              user: this.user,
            })
              .then(() => {
                if (this.productsError) {
                  this.displayError();
                } else {
                  this.fetchTenant();
                  this.fetchTenantList();
                  this.toggleProductForm();
                }
              }).catch(() => {
                if (this.productsError) {
                  this.displayError();
                }
              });
          });
      },
      commitRemoveProduct() {
        this.$store.dispatch('tenants/productDelayed/delete', {
          tenant_id: this.$route.params.id,
          product_id: this.removeProduct.id,
          date_application: this.form.date_application,
          user: this.user,
        });
        this.showConfirmationModal = false;
        this.fetchTenant();
        this.fetchTenantList();
        this.isRemoved = true;
        this.removeProduct = null;
      },
      cancelProductRemove() {
        this.showConfirmationModal = false;
        this.removeProduct = null;
      },
      isEditingIssued() {
        this.isEditingTenant = !this.isEditingTenant;
      },
      showConfirmationForRemovingProduct(product) {
        if (parseInt(product.permits, 10) > 0) {
          this.productWarningHeader = `${this.$t('tenants.removing')}:  ${product.name}`;
          this.productWarningBody = `${this.$t('common.issued_permits')} ${this.$t('products.cannot_remove')}`;
          this.showWarningModal = true;
        } else {
          this.removeProduct = product;
          this.showConfirmationModal = true;
        }
      },
      closeWarningModal() {
        this.showWarningModal = false;
        this.$store.commit(`tenants/product/${mTypes.DATA_ERROR}`, false);
      },
      submitEdit(defferedId, productId) {
        console.log('productId', productId);
  
        if (this.oldValue.toString() === this.$refs[`changeCount${defferedId}`].value) {
          this.setSelectedRow(null);
          return false;
        }
        this.$validator.validateAll()
          .then((success) => {
            if (!success) {
              return;
            }
            const tenantId = parseInt(this.$route.params.id, 10);
            this.$store.dispatch('tenants/productDelayed/edit', {
              productId,
              defferedId,
              companyId: tenantId,
              date_application: this.form.date_application,
              amount: this.permitInput,
              user: this.user,
            })
              .then(() => {
                if (this.productsError) {
                  this.displayError();
                } else {
                  this.fetchTenant();
                  this.fetchTenantList();
                }
              })
              .catch(() => {});
          });
        this.setSelectedRow(null);
        return false;
      },
      displayError() {
        this.productWarningHeader = this.$t('common.error');
        this.showWarningModal = true;
        switch (this.productsError) {
          case 'invalid_product_amount':
            this.productWarningBody = this.$t('errors.invalid_product_amount');
            break;
          case 'product_amount_lower_than_permits_count':
            this.productWarningBody = this.$t('errors.product_amount_lower_than_permits_count');
            break;
          default:
            this.productWarningBody = this.$t('errors.zone_capacity_exceeded');
        }
      },
      editTotalPermitAmount(prop, value) {
        this[prop] = value;
        this.permit_value = this[prop];
      },
      fetchTenant() {
        this.$store.dispatch('tenants/tenant/fetch', { id: this.$route.params.id, user: this.user });
      },
      fetchTenantList() {
        this.$store.dispatch('landlord/tenants/fetch', { user: this.user, limit: this.tenantLimit, keyword: this.keywordFilter });
      },
      setSelectedRow(row) {
        const rowId = row && row.id ? row.id : row;
        this.selectedRow.id = rowId;
        this.selectedRow.tenantId = this.tenant.id;
        this.$nextTick(() => {
          if (this.isSelectedRow(rowId) && rowId) {
            this.$refs[`changeCount${rowId}`].focus();
          }
        });
        this.oldValue = row && row.issued ? row.issued : row;
      },
      isSelectedRow(rowId) {
        if (this.selectedRow.tenantId !== this.tenant.id) {
          this.setSelectedRow(null);
        }
        return this.selectedRow.id === rowId;
      },
      createAmountValidator() {
        this.$validator.extend('cannotBeLower', {
          getMessage: () => 'Number cannot be lower then assigned permits',
          validate: (value) => {
            if (value < 1) {
              return false;
            }
            return true;
          },
        });
      },
      isLengthSmaller(index, length) {
        return index < (length - 1);
      },
      routeToProduct(product) {
        this.$router.push({ name: 'product', params: { id: product.id }, query: { keyword: product.name } });
      },
    },
    data() {
      return {
        showConfirmationModal: false,
        showWarningModal: false,
        removeProduct: null,
        permitInput: null,
        isEditingTenant: false,
        isActive: false,
        isRemoved: false,
        isProductFormOpened: false,
        product: null,
        oldValue: null,
        form: {
          amount: null,
        },
        selectedRow: {
          id: null,
          tenantId: null,
        },
        columns: ['type', 'name', 'zone', 'date', 'count', 'remove_product', 'edit'],
        options: {
          texts: {
            noResults: this.$t('tenants.no_matching_records'),
            count: this.$t('tables.count'),
          },
          perPage: 10,
          filterable: false,
          rowClassCallback: () => 'VueTables__row',
          headings: {
            type: () => this.$t('products.type'),
            name: () => this.$t('common.name'),
            zone: () => this.$t('common.zone'),
            date: () => this.$t('common.date'),
            count: () => this.$t('common.give_out'),
            remove_product: () => (''),
            edit: () => (''),
          },
        },
        timeOptions: {
          disabledDate: (date) => {
            const isInPast = moment(date).isBefore(moment(), 'day');
            if (this.form.startTime) {
              return moment(this.form.startTime).isAfter(moment(date), 'day') || isInPast;
            }
            return isInPast;
          },
          firstDayOfWeek: 1,
        },
      };
    },
  };
</script>
<style lang="scss" scoped>
  @import '~styles/blocks/details.scss';
  .details-component__listWidget {
    width: 50%;
  }

  .link {
    .icon--plus {
      position: relative;
      top: -1px;
      margin: 0 5px;
      font-weight: 900;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/tenants/ProductsDelayedTable.vue